import React from 'react';
import PropTypes from 'prop-types';
import { useCombobox } from 'downshift';
import { matchSorter } from 'match-sorter';
import { Wrapper, InputWrapper, CloseButton, SearchInput, Menu, Item, MenuWrapper } from './styles';
import { SvgChevron, SvgX } from '../../svg';
import { useTheme } from '../../theme';

const defaultItemToString = (i) => i;

function ComboboxSelect(props) {
  const {
    items,
    itemToString: oldItemToString = defaultItemToString,
    onChange,
    onTouch = () => {},
    initialSelectedItem,
    selectedItem,
    initialInputValue = selectedItem ? oldItemToString(selectedItem) : '',
    placeholder = 'Select...',
    searchKeys = [],
    style,
    testid = 'combobox-select',
    clearInputOnChange,
    autoFocus,
    disabled,
  } = props;

  const theme = useTheme();

  const [inputItems, setInputItems] = React.useState(items);
  React.useEffect(() => {
    setInputItems(items);
  }, [items]);

  const itemToString = React.useCallback(
    (v) => {
      if (v != null) {
        return oldItemToString(v);
      }
      return v;
    },
    [oldItemToString]
  );

  function stateReducer(state, { type, changes }) {
    switch (type) {
      case useCombobox.stateChangeTypes.InputChange:
        return {
          ...changes,
          selectedItem: changes.inputValue === '' ? null : changes.selectedItem,
        };
      case useCombobox.stateChangeTypes.InputBlur:
        return {
          ...changes,
          inputValue: changes.selectedItem ? itemToString(changes.selectedItem) : '',
        };
      default:
        return changes;
    }
  }

  const inputRef = React.useRef();
  const isInputFocused = () => document.activeElement === inputRef.current;

  React.useEffect(() => {
    if (autoFocus && inputRef?.current) inputRef.current.focus();
  }, [autoFocus]);

  // to avoid the select always being controlled
  const comboboxProps = {};
  if (typeof selectedItem !== 'undefined') {
    comboboxProps.selectedItem = selectedItem;
  }
  if (initialInputValue) {
    comboboxProps.initialInputValue = initialInputValue;
  }

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
    openMenu,
  } = useCombobox({
    onSelectedItemChange: (changes) => {
      // FIXME: https://github.com/downshift-js/downshift/issues/874
      if (changes.selectedItem) {
        setTimeout(() => onChange(changes.selectedItem));
        if (clearInputOnChange) {
          setInputValue('');
        }
      }
    },
    onInputValueChange: ({ inputValue }) => {
      setInputItems(
        matchSorter(items, inputValue, {
          keys: [itemToString, ...searchKeys],
        })
      );
    },
    initialSelectedItem,
    items: inputItems,
    itemToString,
    stateReducer,
    ...comboboxProps,
  });

  function clearSelection() {
    onChange(null);
    setInputValue('');
  }

  React.useEffect(() => {
    if (selectedItem) {
      onChange(selectedItem);
      setInputValue(itemToString(selectedItem));
    }
    // setInputValue triggers the useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemToString, onChange, selectedItem]);

  return (
    <>
      <Wrapper
        isFocused={isInputFocused()}
        disabled={disabled}
        isMenuOpen={isOpen}
        {...getComboboxProps({
          onClick: () => {
            inputRef.current.focus();
            if (!isOpen) openMenu(true);
          },
        })}
        style={style}
      >
        <InputWrapper>
          <SearchInput
            onFocus={onTouch}
            data-testid={`${testid}-input`}
            visuallyHidden={false}
            placeholder={placeholder}
            style={{ width: '100%' }}
            {...getInputProps({ ref: inputRef, disabled })}
          />
          {selectedItem ? (
            <CloseButton
              type="button"
              {...getToggleButtonProps({ disabled })}
              onClick={clearSelection}
              data-testid={`${testid}-menu-button`}
            >
              <SvgX />
            </CloseButton>
          ) : (
            <CloseButton
              type="button"
              {...getToggleButtonProps({ disabled })}
              data-testid={`${testid}-menu-button`}
            >
              <SvgChevron stroke={theme.colors.primary20b} direction={isOpen ? 'top' : 'bottom'} />
            </CloseButton>
          )}
        </InputWrapper>
        <MenuWrapper {...getMenuProps()} data-testid={`${testid}-menu`}>
          {isOpen ? (
            <Menu>
              {inputItems.map((item, index) => (
                <Item
                  data-testid={`${testid}-item-${index}`}
                  highlighted={highlightedIndex === index}
                  bold={itemToString(item) === itemToString(selectedItem)}
                  key={index}
                  {...getItemProps({
                    item,
                    index,
                  })}
                >
                  {itemToString(item)}
                </Item>
              ))}
            </Menu>
          ) : null}
        </MenuWrapper>
      </Wrapper>
    </>
  );
}

ComboboxSelect.propTypes = {
  itemToString: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onTouch: PropTypes.func,
  searchKeys: PropTypes.array,
  items: PropTypes.array.isRequired,
  initialSelectedItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  selectedItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  initialInputValue: PropTypes.string,
  placeholder: PropTypes.string,
  testid: PropTypes.string,
  style: PropTypes.object,
  clearInputOnChange: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default React.memo(ComboboxSelect);
